import React, { useEffect, useRef } from 'react';

const DocumentViewer = ({ isOpen, onClose, document: doc }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !doc) return null;

  // Determine file type and extension
  const getFileType = (url) => {
    if (!url) return 'unknown';
    const extension = url.split('.').pop().toLowerCase().split('?')[0]; // Remove query params
    
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) return 'image';
    if (['doc', 'docx'].includes(extension)) return 'word';
    if (['xls', 'xlsx'].includes(extension)) return 'excel';
    if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
    if (['html', 'htm'].includes(extension)) return 'html';
    if (['txt'].includes(extension)) return 'text';
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'ogg', 'flac'].includes(extension)) return 'audio';
    
    return 'unknown';
  };

  const fileType = getFileType(doc.url);
  const fileName = doc.name || 'Document';

  const renderDocument = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <iframe
            src={doc.url || `https://docs.google.com/viewer?url=${encodeURIComponent(doc.url)}&embedded=true`}
            className="w-full h-full rounded-lg"
            title={fileName}
          />
        );

      case 'image':
        return (
          <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg">
            <img 
              src={doc.url} 
              alt={fileName}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center h-full bg-black rounded-lg">
            <video 
              controls 
              className="max-w-full max-h-full rounded-lg"
            >
              <source src={doc.url} />
              Your browser does not support the video tag.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded-lg p-8">
            <div className="mb-6">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <path d="M40 73.3333C58.41 73.3333 73.3333 58.41 73.3333 40C73.3333 21.59 58.41 6.66666 40 6.66666C21.59 6.66666 6.66666 21.59 6.66666 40C6.66666 58.41 21.59 73.3333 40 73.3333Z" stroke="#40C191" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M50 36.6667V30C50 25 48.3333 23.3333 43.3333 23.3333H36.6667C31.6667 23.3333 30 25 30 30V50C30 55 31.6667 56.6667 36.6667 56.6667H43.3333C48.3333 56.6667 50 55 50 50V46.6667" stroke="#40C191" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <audio controls className="w-full max-w-md">
              <source src={doc.url} />
              Your browser does not support the audio tag.
            </audio>
          </div>
        );

      case 'text':
        return (
          <iframe
            src={doc.url}
            className="w-full h-full rounded-lg bg-white"
            title={fileName}
          />
        );

      case 'html':
        return (
          <iframe
            src={doc.url}
            className="w-full h-full rounded-lg bg-white"
            title={fileName}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        );

      case 'word':
      case 'excel':
      case 'powerpoint': {
        // Use Microsoft Office Online Viewer for preview
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(doc.url)}`;
        return (
          <div className="w-full h-full flex flex-col">
            <iframe
              src={viewerUrl}
              className="w-full h-full rounded-lg border-0"
              title={fileName}
              allow="fullscreen"
            />
            <div className="mt-4 flex justify-center">
              <a
                href={doc.url}
                download={fileName}
                className="px-6 py-2 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
              >
                Download {fileType === 'word' ? 'Word' : fileType === 'excel' ? 'Excel' : 'PowerPoint'} Document
              </a>
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded-lg p-8">
            <div className="mb-6">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                <path d="M46.6667 10H20C18.2319 10 16.5362 10.7024 15.286 11.9526C14.0357 13.2029 13.3333 14.8986 13.3333 16.6667V63.3333C13.3333 65.1014 14.0357 66.7971 15.286 68.0474C16.5362 69.2976 18.2319 70 20 70H60C61.7681 70 63.4638 69.2976 64.714 68.0474C65.9643 66.7971 66.6667 65.1014 66.6667 63.3333V26.6667L46.6667 10Z" stroke="#73827c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M46.6667 10V26.6667H66.6667" stroke="#73827c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{fileName}</h3>
            <p className="text-sm text-gray-600 mb-6">Preview not available for this file type</p>
            <a
              href={doc.url || '#'}
              download={fileName}
              className="px-6 py-3 bg-emerald-400 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors shadow-sm"
            >
              Download Document
            </a>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div ref={modalRef} className="bg-white rounded-2xl w-full max-w-6xl h-[90vh] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{fileName}</h2>
            {doc.detail && (
              <p className="text-sm text-gray-600 mt-1">{doc.detail}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {doc.url && (
              <a
                href={doc.url}
                download={fileName}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Download
              </a>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Document Content */}
        <div className="flex-1 p-6 overflow-hidden">
          {renderDocument()}
        </div>

        {/* Footer with Document Info */}
        {(doc.fee || doc.dateCreated || doc.user) && (
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center gap-6 text-sm">
              {doc.fee && (
                <div>
                  <span className="text-gray-500">Fee: </span>
                  <span className="font-semibold text-gray-900">{doc.fee}</span>
                </div>
              )}
              {doc.dateCreated && (
                <div>
                  <span className="text-gray-500">Created: </span>
                  <span className="text-gray-900">{doc.dateCreated}</span>
                </div>
              )}
              {doc.user && (
                <div>
                  <span className="text-gray-500">Last Updated by: </span>
                  <span className="text-gray-900">{doc.user}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;

